/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import { useSelector } from 'react-redux';

import tenantUserServices from '../../../services/clientAdmin/tenantUserServices';
import Page from '../../../components/Page';
import ResponsiveTable from '../../../components/Table/Responsivetable';
import AddLearnerDrawer from '../modals/AddLearnerDrawer';
import DeactivateLearnerModal from '../modals/DeactivateLearnerModal';
import ReactivateLearnerModal from '../modals/ReactivateLearnerModal';

const STATUS_TABS = [
  { label: 'All',      value: '' },
  { label: 'Active',   value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];

const PAGE_SIZE = 15;

function Avatar({ name }) {
  const parts = (name || '').trim().split(' ');
  const initials = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  const colors = [
    'bg-purple-100 text-purple-700', 'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',   'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
  ];
  const color = colors[(initials.charCodeAt(0) || 0) % colors.length];
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold ${color} flex-shrink-0`}>
      {initials.toUpperCase() || '?'}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.856-1.487M15 10a3 3 0 11-6 0 3 3 0 016 0zM6 20a9 9 0 0118 0v2h-2v-2a7 7 0 00-14 0v2H6v-2z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">No Learners Found</h3>
      <p className="text-gray-600 text-sm text-center max-w-sm">
        No learners match your current filter or search.
      </p>
    </div>
  );
}

function TableSkeleton() {
  return (
    <>
      <div className="hidden lg:block">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['NAME', 'EMAIL', 'DEPARTMENT', 'TEAM', 'STATUS', 'ACTIONS'].map(h => (
                <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {Array.from({ length: 7 }).map((_, i) => (
              <tr key={i} className="animate-pulse">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" />
                    <div className="h-3 w-32 bg-gray-200 rounded" />
                  </div>
                </td>
                <td className="px-6 py-4"><div className="h-3 w-40 bg-gray-200 rounded" /></td>
                <td className="px-6 py-4"><div className="h-3 w-24 bg-gray-200 rounded" /></td>
                <td className="px-6 py-4"><div className="h-3 w-20 bg-gray-200 rounded" /></td>
                <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded-full" /></td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <div className="h-6 w-20 bg-gray-200 rounded" />
                    <div className="h-6 w-20 bg-gray-200 rounded" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-4 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="h-3 w-36 bg-gray-200 rounded" />
            </div>
            <div className="h-3 w-48 bg-gray-200 rounded" />
            <div className="h-3 w-28 bg-gray-200 rounded" />
            <div className="flex gap-2 pt-1">
              <div className="h-5 w-16 bg-gray-200 rounded-full" />
              <div className="h-6 w-20 bg-gray-200 rounded ml-auto" />
              <div className="h-6 w-20 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default function TenantUsers() {
  const navigate  = useNavigate();
  const clientId  = useSelector((state) => state.userInfo?.clientId);

  const [users, setUsers]         = useState([]);
  
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState('');
  const [loading, setLoading]     = useState(true);
  const [summary, setSummary]     = useState({ active: 0, total: 0, loaded: false });

  const [showAddDrawer, setShowAddDrawer]             = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [selectedLearner, setSelectedLearner]         = useState(null);

  const searchTimer = useRef(null);
  const isFirstMount = useRef(true);

  // ── Single fetch function — fetches table data + summary together ──────────
  const fetchAll = useCallback(async (pg, q, st, refreshSummary = false) => {
    setLoading(true);
    try {
      const promises = [
        tenantUserServices.getTenantUsers(clientId, pg - 1, PAGE_SIZE, q, 'AUTH_USER', st),
      ];

      // Only fetch summary counts when needed (initial load, search change, add/deactivate/reactivate)
      if (refreshSummary) {
        promises.push(
          tenantUserServices.getTenantUsers(clientId, 0, 1, q, 'AUTH_USER', 'ACTIVE')
        );
      }

      const [tableRes, activeRes] = await Promise.all(promises);

      if (tableRes.ok) {
        setUsers(tableRes.data.users || []);
        setTotal(tableRes.data.total || 0);

        // Reuse total from table response when on "All" tab
        if (refreshSummary) {
          setSummary({
            total:  st === '' ? (tableRes.data.total || 0) : summary.total,
            active: activeRes?.ok ? (activeRes.data.total || 0) : summary.active,
            loaded: true,
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // ── Initial mount only ────────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    fetchAll(1, '', '', true);
  }, [clientId]);

  // ── Tab change — only refresh table, keep summary ─────────────────────────
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(1);
    fetchAll(1, search, statusFilter, false);
  }, [statusFilter]);

  // ── Debounced search — refresh both table and summary ─────────────────────
  useEffect(() => {
    if (isFirstMount.current) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchAll(1, search, statusFilter, true);
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAddLearner = async (formValues) => {
    const res = await tenantUserServices.createTenantUser({
      firstName:          formValues.firstName,
      lastName:           formValues.lastName,
      email:              formValues.email,
      phone:              formValues.phone || null,
      password:           formValues.password,
      roleCode:           'AUTH_USER',
      teamId:             formValues.teamId ? Number(formValues.teamId) : null,
      employeeRollNumber: formValues.employeeRollNumber || null,
      notes:              formValues.notes || null,
    });
    if (!res.ok) throw new Error(res.data?.message || 'Failed to add learner');
    setShowAddDrawer(false);
    setPage(1);
    await fetchAll(1, search, statusFilter, true);
  };

  const handleDeactivate = async () => {
    if (!selectedLearner) return;
    const res = await tenantUserServices.toggleTenantUser(selectedLearner.id, false);
    if (res.ok) {
      setShowDeactivateModal(false);
      setSelectedLearner(null);
      await fetchAll(page, search, statusFilter, true);
    }
  };

  const handleReactivate = async () => {
    if (!selectedLearner) return;
    const res = await tenantUserServices.toggleTenantUser(selectedLearner.id, true);
    if (res.ok) {
      setShowReactivateModal(false);
      setSelectedLearner(null);
      await fetchAll(page, search, statusFilter, true);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchAll(newPage, search, statusFilter, false);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const tableColumns = [
    {
      key: 'name',
      label: 'NAME',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Avatar name={`${row.firstName} ${row.lastName}`} />
          <span className="text-gray-900 font-medium">{row.firstName} {row.lastName}</span>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'EMAIL',
      render: (row) => <span className="text-gray-600 text-sm">{row.email}</span>,
    },
    {
      key: 'departmentName',
      label: 'DEPARTMENT',
      render: (row) => <span className="text-gray-600 text-sm">{row.departmentName || '—'}</span>,
    },
    {
      key: 'teamName',
      label: 'TEAM',
      render: (row) => <span className="text-gray-600 text-sm">{row.teamName || '—'}</span>,
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (row) => (
        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
          row.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
        }`}>
          {row.status === 'ACTIVE' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/app/learner-profile?id=${row.id}`)}
            className="px-4 py-1 text-xs font-normal text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-all duration-200"
          >
            View Profile
          </button>
          {row.status === 'ACTIVE' ? (
            <button
              type="button"
              onClick={() => { setSelectedLearner(row); setShowDeactivateModal(true); }}
              className="px-4 py-1 text-xs font-normal text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-all duration-200"
            >
              Deactivate
            </button>
          ) : ( 
            row?.lastlogin &&
            <button
              type="button"
              onClick={() => { setSelectedLearner(row); setShowReactivateModal(true); }}
              className="px-4 py-1 text-xs font-normal text-green-600 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-all duration-200"
            >
              Reactivate
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Page title="Learners">
      <div className="p-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Learners</h1>
            <p className="text-gray-600 text-sm">
              {summary.loaded ? `${summary.active} active · ${summary.total} total` : '…'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddDrawer(true)}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors text-sm whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add Learner
          </button>
        </div>

        {/* Tabs + Search */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex gap-2">
            {STATUS_TABS.map(tab => (
              <button
                type="button"
                key={tab.value}
                onClick={() => setStatus(tab.value)}
                className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                  statusFilter === tab.value
                    ? 'bg-gray-200 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-md ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, department..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <TableSkeleton />
          ) : (
            <ResponsiveTable
              columns={tableColumns}
              data={users}
              loading={false}
              emptyStateComponent={EmptyState}
              comingfrom="TenantUsers"
            />
          )}
        </div>

        {/* Pagination */}
        {!loading && users.length > 0 && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} learners
            </div>
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <div className="px-4 py-2 text-sm font-medium">
                {page} / {totalPages}
              </div>
              <button
                type="button"
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Modals */}
        <AddLearnerDrawer
          isOpen={showAddDrawer}
          onClose={() => setShowAddDrawer(false)}
          onSubmit={handleAddLearner}
        />

        {showDeactivateModal && selectedLearner && (
          <DeactivateLearnerModal
            learner={selectedLearner}
            onClose={() => { setShowDeactivateModal(false); setSelectedLearner(null); }}
            onConfirm={handleDeactivate}
          />
        )}

        {showReactivateModal && selectedLearner && (
          <ReactivateLearnerModal
            learner={selectedLearner}
            onClose={() => { setShowReactivateModal(false); setSelectedLearner(null); }}
            onConfirm={handleReactivate}
          />
        )}
      </div>
    </Page>
  );
}